import type { FC } from 'react';

import { CheckCircleIcon, LockClosedIcon } from '@heroicons/react/24/solid';

import type { CommunitySpace } from '~/interfaces/community';

import { Spinner } from '../../ui/Spinner';

const CHECK_STAGGER_MS = 80;
const LAST_STAGGERED_ROW = 8;

interface ChannelAccessRowsProps {
  channels: Pick<CommunitySpace, 'id' | 'name'>[];
  /** `stalled` is a pending row after the access poll gave up: still locked, with nothing left in progress. */
  status: 'pending' | 'stalled' | 'unlocked';
}

export const ChannelAccessRows: FC<ChannelAccessRowsProps> = ({ channels, status }) => (
  <ul className="divide-y divide-black/5 rounded-2xl border border-black/10">
    {channels.map((channel, index) => (
      <li key={channel.id} className="flex items-center gap-4 px-4 py-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-black/[0.04]">
          {status === 'unlocked' ? (
            <CheckCircleIcon
              aria-hidden="true"
              className="size-5 text-emerald-600 transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] [@starting-style]:scale-50 [@starting-style]:opacity-0 motion-reduce:transition-none"
              style={{ transitionDelay: `${Math.min(index, LAST_STAGGERED_ROW) * CHECK_STAGGER_MS}ms` }}
            />
          ) : (
            <LockClosedIcon aria-hidden="true" className="size-4 text-black/60" />
          )}
        </span>
        <span className="min-w-0 flex-1 truncate text-base font-medium text-black">{channel.name}</span>
        {status === 'pending' && <Spinner className="size-5 shrink-0" />}
      </li>
    ))}
  </ul>
);
